import { MemoizedSelector, MemoizedSelectorWithProps, createSelector } from '@ngrx/store';
import { StateUtils } from '@spartacus/core';
import {
  EntriesState,
  PagesState,
  PaginationState,
  SearchPageResultState,
  SearchState,
} from '../../../../core/features/store/base-feature-state';
import { Pagination, SoldTo } from '../../../../core/model';
import { PyStateUtils } from '../../../../core/state';
import { AvailableSearchParamsState, AvailableState, SoldToState, StateWithSoldTo } from '../sold-to-state';
import { getSoldToState } from './feature.selector';

export const getAvailableSoldTosOnUserDetailsState: MemoizedSelector<StateWithSoldTo, AvailableState> = createSelector(
  getSoldToState,
  (state: SoldToState) => state && state.availableOnUserDetails
);

export const getAvailableOnUserDetailsSearchResultEntities: MemoizedSelector<StateWithSoldTo, EntriesState<SoldTo> | undefined> =
  createSelector(getAvailableSoldTosOnUserDetailsState, (state: AvailableState) => (state && state.entities) || undefined);

export const getAvailableOnUserDetailsSearchResultEntries: MemoizedSelectorWithProps<StateWithSoldTo, any, SoldTo[] | undefined> =
  createSelector(getAvailableOnUserDetailsSearchResultEntities, (entries: EntriesState<SoldTo>, props) => {
    return (props.keys && entries && props.keys.map((i: string) => entries[i])) || undefined;
  });

export const getAvailableOnUserDetailsSearchParamsSelector: MemoizedSelector<StateWithSoldTo, AvailableSearchParamsState> =
  createSelector(getAvailableSoldTosOnUserDetailsState, (state: AvailableState) => (state && state.searchParams) || undefined);

export const getAvailableOnUserDetailsSearchSelector: MemoizedSelector<
  StateWithSoldTo,
  SearchState<SearchPageResultState> | undefined
> = createSelector(getAvailableSoldTosOnUserDetailsState, (state: AvailableState) => (state && state.search) || undefined);

export const getAvailableOnUserDetailsSearchPaginationStateSelector: MemoizedSelector<
  StateWithSoldTo,
  PaginationState<SearchPageResultState> | undefined
> = createSelector(
  getAvailableOnUserDetailsSearchSelector,
  getAvailableOnUserDetailsSearchParamsSelector,
  (state: SearchState<SearchPageResultState>, searchParams: AvailableSearchParamsState) => {
    return (searchParams && state && state[searchParams.key]) || undefined;
  }
);

export const getAvailableOnUserDetailsSearchPaginationSelector: MemoizedSelector<StateWithSoldTo, Pagination | undefined> =
  createSelector(getAvailableOnUserDetailsSearchPaginationStateSelector, (state) => (state && state.pagination) || undefined);

export const getAvailableOnUserDetailsSearchPagesSelector: MemoizedSelector<
  StateWithSoldTo,
  PagesState<SearchPageResultState> | undefined
> = createSelector(
  getAvailableOnUserDetailsSearchParamsSelector,
  getAvailableOnUserDetailsSearchPaginationStateSelector,
  (queryParams: AvailableSearchParamsState, paginationState: PaginationState<SearchPageResultState>) => {
    return (queryParams && paginationState && paginationState.pages) || undefined;
  }
);

// Gets results only from the current pagination page
export const getAvailableOnUserDetailsSearchPageResultsSelector: MemoizedSelector<
  StateWithSoldTo,
  StateUtils.LoaderState<SearchPageResultState>
> = createSelector(
  getAvailableOnUserDetailsSearchParamsSelector,
  getAvailableOnUserDetailsSearchPagesSelector,
  (queryParams: AvailableSearchParamsState, pagesState: PagesState<SearchPageResultState>) => {
    return (queryParams && pagesState && pagesState[queryParams.page]) || PyStateUtils.initialLoaderState;
  }
);

// Gets array of IDs of all results between first and current pagination pages
export const getAvailableOnUserDetailsCombinedSearchPageResultsIdsSelector: MemoizedSelector<
  StateWithSoldTo,
  Array<string>
> = createSelector(
  getAvailableOnUserDetailsSearchParamsSelector,
  getAvailableOnUserDetailsSearchPagesSelector,
  (queryParams: AvailableSearchParamsState, pagesState: PagesState<SearchPageResultState>) => {
    const combinedResults: Array<string> = [];
    if (queryParams && pagesState) {
      [...Array(queryParams.page + 1).keys()].forEach((pageNumber) => {
        if (pagesState[pageNumber]) {
          combinedResults.push(...StateUtils.loaderValueSelector(pagesState[pageNumber]).results);
        }
      });
    }
    return combinedResults;
  }
);
